import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Sparkles, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Digest() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/digest/latest");
      setDigest(data?.empty ? null : data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/generate_now");
      setDigest(data);
      toast.success("Fresh digest generated");
    } catch { toast.error("Could not generate"); }
    finally { setBusy(false); }
  };

  if (loading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="digest-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-caps mb-2 flex items-center gap-1.5"><Mail className="h-3 w-3 text-primary" />Monday Tribunal</div>
          <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">Your weekly digest.</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Every Monday, one grounded insight for your vocation plus three chapters worth reading this week.
          </p>
        </div>
        <Button onClick={generate} disabled={busy} className="rounded-full" data-testid="digest-generate">
          <Sparkles className="h-4 w-4 mr-1" />{busy ? "Composing…" : "Generate now"}
        </Button>
      </div>

      {!digest ? (
        <Card className="p-10 text-center">
          <Mail className="h-7 w-7 mx-auto mb-3 text-muted-foreground" />
          <div className="font-medium">No digest yet.</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Generate one now, or wait for Monday — the tribunal delivers automatically.
          </p>
        </Card>
      ) : (
        <Card className="p-8 md:p-10">
          <div className="label-caps mb-2">Insight for a {digest.vocation}</div>
          <p className="mt-4 text-base md:text-lg leading-relaxed whitespace-pre-wrap">{digest.insight}</p>
          <div className="mt-10 pt-8 border-t border-border">
            <div className="label-caps mb-3">Three chapters for this week</div>
            <div className="space-y-2">
              {(digest.recommended || []).map((c) => (
                <Link key={c.id} to={`/app/learn/chapter/${c.id}`} data-testid={`digest-ch-${c.id}`}
                  className="flex items-start justify-between gap-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors duration-200">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.topic}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-6 text-xs text-muted-foreground">
            {digest.sent_at ? `Emailed ${new Date(digest.sent_at).toLocaleString()}` : "Not emailed yet — next Monday delivery scheduled."}
          </div>
        </Card>
      )}
    </div>
  );
}
